import { Config } from 'config';

import { forEach, find, filter, head, last,
         sortedIndexBy, sortedIndex } from 'lodash';

import { invalidateOwner, deg2rad, Magnitude } from 'gear';

import { Xml } from '../util';

import { Point, Matrix, Segment, Cubic, Bounds, Frame, Curve,
         BoundsBuilder, CurveArcLenCursor } from '../geometry';

import { TextLayout, TextChar, GShape, toSingleGShape } from '../figure';

import { Process } from './process';
import { Envelope } from './envelope';


// Constrain
//===============================================================================================

// Scale Chars using PivotedScaler, that works when using together
// with FitOnPath, and after BookEnd

// Only works for lineDirection % PI/2 == 0
const scaleChars = function(layout,sx,sy) {

  const lines = layout.textLines();
  const pivot = layout.pivot();
  const isHorizontal = !layout.text.isVertical();

  for(let i = 0, iEnd = lines.length; i < iEnd ; ++ i ){
    const lineChars = lines[i]._chars,
          count = lineChars.length;

    let accExtraKerning = 0,
        accOriginalExtraKerning = 0;

    if( count > 0 ){
      // const totalShift = 0;
      const lineIdx = i+1;
      for( let j = 0; j < count; ++j){
        const char = lineChars[j];
        const idx = j+1;

        // Pick the scaling command for this char, if any
        const charData = layout.charData(lineIdx,idx);

        const transformers = charData._transformers;

        transformers.push( new TextChar.PivotedScaler(pivot,sx,sy) );

        const originalExtraKerning = charData._extraKerning;
        const previousHPos = char._originalHPos + accOriginalExtraKerning + originalExtraKerning;
        accOriginalExtraKerning += originalExtraKerning;

        const desiredHPos = previousHPos * (isHorizontal ? sx : sy);
        const diffHPos = (desiredHPos - previousHPos) - accExtraKerning;

        charData._extraKerning += diffHPos;

        accExtraKerning += diffHPos;
      }
    }

    layout.invalidate();
  }
};

function computeScaleRange( size, minSize, maxSize ) {
  return {
    min: minSize > 0 ? minSize / size : 0,
    max: maxSize > 0 ? maxSize / size : Number.MAX_VALUE
  };
}

function computeScale( range, fit ) {

  let s_min = range.min,
      s_max = range.max;

  if( s_min > s_max ) {
    // We can not respect the constrain: MaxWidth, MaxHeight takes precedence
    // ignore s_min
    s_min = 0;
  }

  switch( fit ) {

    case 'Stretched':
      return s_max;

    case 'Compressed':
      return s_min;

    default:
      // Use minimun deformation
      return Math.min(Math.max(s_min,1),s_max);
  }
}

function effectiveFit( fit, minSize, maxSize ) {
  return ( ( minSize === 0 && fit === 'Compressed' ) ||
           ( maxSize === 0 && fit === 'Stretched'  )  ) ? 'Contained' : fit;
}

const Constrain = Process.Constrain = Process.extend({ typeName: 'ConstrainProcess',

  type: 'Constrain',

  Properties: {
    maxWidth   : { type: 'magnitude' , def: new Magnitude(0,'mm'), onChange: invalidateOwner, dropDef:true },
    maxHeight  : { type: 'magnitude' , def: new Magnitude(0,'mm'), onChange: invalidateOwner, dropDef:true },
    minWidth   : { type: 'magnitude' , def: new Magnitude(0,'mm'), onChange: invalidateOwner, dropDef:true },
    minHeight  : { type: 'magnitude' , def: new Magnitude(0,'mm'), onChange: invalidateOwner, dropDef:true },
    isometric  : { type: 'boolean', def: false      , onChange: invalidateOwner, dropDef:true },
    hFit       : { type: 'string' , def: 'Contained', onChange: invalidateOwner, dropDef:true },
    vFit       : { type: 'string' , def: 'Contained', onChange: invalidateOwner, dropDef:true }
  },

  outputsText() {
    return true;
  },

  canProcess(/*figure*/) {
    return true;
  },

  // Guard against setting stretching and compressing
  // without a Max or Min value

  get effectiveHFit() {
    return effectiveFit(this.hFit,this.minWidth,this.maxWidth);
  },
  get effectiveVFit() {
    return effectiveFit(this.vFit,this.minHeight,this.maxHeight);
  },

  get effectiveIsometricFit() {
    const hFit = this.effectiveHFit,
          vFit = this.effectiveVFit;

    // Stretched takes precedence
    return ( hFit === 'Stretched'  || vFit === 'Stretched'  ) ? 'Stretched'  :
           ( hFit === 'Compressed' || vFit === 'Compressed' ) ? 'Compressed' :
                                                                'Contained'  ;
  },

  createTransform: function(bounds) {

    const scaleRange_x = computeScaleRange( bounds.width , this.minWidth , this.maxWidth ),
          scaleRange_y = computeScaleRange( bounds.height, this.minHeight, this.maxHeight);

    let sx, sy;

    if( this.isometric ) {

      const scaleRange = {
        min: Math.max( scaleRange_x.min, scaleRange_y.min ),
        max: Math.min( scaleRange_x.max, scaleRange_y.max )
      };

      sx = sy = computeScale(scaleRange,this.effectiveIsometricFit);
    }
    else {
      sx = computeScale(scaleRange_x,this.effectiveHFit);
      sy = computeScale(scaleRange_y,this.effectiveVFit);
    }

    return { sx: sx, sy: sy };
  },

  process( c ) {
    const figures = c.figures;
    const outFigures = [];

    const bounds = computeBounds( toGShapesAndRasterBounds(figures) );

    const constrain = this.createTransform(bounds);
    const constrainMatrix = Matrix.scale(constrain.sx,constrain.sy);

    forEach(figures, figure => {
      let figureToChangePen = figure;

      if( figure instanceof TextLayout ) { // &&  ) {
        //we cant modify pen if we do this
        //if( this.keepTextFor(figure) ) {
          //scaleChars(figure,constrain.sx,constrain.sy);
          //outFigures.push(figure);
        //}
        //else 
        {

          // Give up... constrain like a shape, loose textLayout
          const outFigure = figure.toGShape();
          figureToChangePen = outFigure;
          modifyCurves( outFigure, c => {
            // do not call .transform because it modifies powerclips,
            // we will clean up this in future versions
            c.transform(constrainMatrix);
          });
          outFigures.push(outFigure);

          // Transform the basic points of the textLayout so
          // we are able to edit this text inline
          figure.transformBasicPoints(constrainMatrix);
        }
      }
      else if( figure instanceof GShape ){
        modifyCurves(figure,function(c){
          // do not call .transform because it modifies powerclips,
          // we will clean up this in future versions
          c.transform(constrainMatrix);
        });
        outFigures.push(figure);
      }
      else {
        figure.transform(constrainMatrix);
        outFigures.push(figure);
      }

      forEach(figureToChangePen.polyregions, p => {        
        if( p.pen && p.pen._type === 'Pen' && p.pen._width > 0.0 ) {
          var pen = p.pen.clone();
          pen.width =  pen.width * constrain.sx;
          p.pen = pen;
        }
      });
      
      if (figureToChangePen.pen && figureToChangePen.pen._type === 'Pen' && figureToChangePen.pen._width > 0.0 ) {
        var pen = figureToChangePen.pen.clone();
        pen.width = pen.width * constrain.sx;
        figureToChangePen.pen = pen;
      }

    });

    // [TODO] Check TextLayout

    const anchor = constrainMatrix.apply( c.anchor );

    setRenderFrame( filter( figures, figure => figure instanceof GShape ),
                    Frame.fromBounds( bounds.scalingTransform(constrainMatrix) ) );

    c.figures = outFigures;
    c.anchor = anchor;
    c.preScale = constrainMatrix.apply(c.preScale);

  },

  // This is horrible but I do not see a way out for the moment without
  // removing the extraKerning abstraction
  // We only support
  //  Constrain => BookEnd => ... tex   => FitOnPath is fine
  //  BookEnd => Constrain => ... shape => VerticalArch is fine
  keepTextFor: function(textLayout) {
    const ldv = textLayout.text.lineDirectionVector();
    return textLayout.textLines().length === 1 &&
           (Math.abs(ldv.x) < 1E-10 || Math.abs(ldv.y) < 1E-10) &&
      ! find( textLayout.textLines(), textLine => {
      return find( textLine._chars, char => {
        return char._charData._transformers.length > 0;
      });
    });
  }
});

Xml.addFromBuild(Constrain);



// VerticalArch
//===============================================================================================

const modifyCurves = function(figure,modifier) {
  const { polyregions } = figure;
  for(let p = 0, pEnd = polyregions.length; p < pEnd; ++p ) {
    const polyregion = polyregions[p];
    const regions = polyregion._regions;
    for(let r = 0, rEnd = regions.length; r < rEnd; ++r ) {
      const curves = regions[r].curves;
      for(let c = 0, cEnd = curves.length; c < cEnd; ++c ) {
        modifier(curves[c]);
      }
    }
    polyregion.invalidate();
  }
  return figure;
};

const convertSegmentsToBeziers = function( figure ) {
  return modifyCurves( figure.clone(), c => {
    const pieces = c.pieces;
    for(let i = 0, iEnd = pieces.length; i < iEnd; ++i ) {
      const piece = pieces[i];
      if( piece instanceof Segment ) {
        const P0 = piece.P0,
            P3 = piece.P1;
        const P1 = Point.sm(P0,2).add(P3).sm(1/3),
            P2 = Point.add(P0,Point.sm(P3,2)).sm(1/3);

        pieces[i] = new Cubic( P0, P1, P2, P3 );
      }
    }
  });
};

const preprocessInput = function( figure, highResolution, keepLines, polyParams ) {
  let target = null;
  if( highResolution ) {
    throw('local highResolution not supported');
    // Not supported...
    const poly = figure.polygonize(polyParams); // [TODO]
    if( poly ) {
      target = keepLines ? poly : poly.supersamplePolyline(); // [TODO]
    }
  }
  else {
    target = keepLines ? figure.clone() : convertSegmentsToBeziers(figure);
  }
  return target;
};

const BC_VerticalArchTransformation = function( inLX, inHX, ellWidth, ellHeight, arcStartDeg, arcEndDeg, isometric ) {
  // g.assert( inLX < inHX ) ;

  this._isometric = isometric === undefined ? false : isometric;

  this._inMX = ( inLX + inHX ) / 2 ;

  const arcStart = deg2rad(arcStartDeg),
        arcEnd   = deg2rad(arcEndDeg);

  // g.assert(arcStart >= 0 && arcStart <= 2 * Math.PI ) ;
  // g.assert(arcEnd   >= 0 && arcEnd   <= 2 * Math.PI ) ;

  // g.assert( ( arcStart <= Math.PI && arcStart <= Math.PI ) ||
  //            ( arcStart >  Math.PI && arcStart >  Math.PI ) ) ;

  const upper = this._upper = arcStart > arcEnd ;

  // g.assert( upper ? arcStart > arcEnd : arcStart < arcEnd ) ;

  const sweep = upper ? arcStart - arcEnd : arcEnd - arcStart ;

  this._arcMiddle = ( arcStart + arcEnd ) / 2 ;

  const xAxis = this._xAxis = ellWidth  / 2 ;
              this._yAxis = ellHeight / 2 ;

  this._xCircleArcLen = sweep * xAxis ;

  const domLX = xAxis * Math.cos(arcStart) ;
  const domHX = xAxis * Math.cos(arcEnd) ;

  // g.assert(domLX < domHX ) ;

  const domWidth = this._domWidth = domHX - domLX ;

  const inWidth = inHX - inLX ;

  this._scale = ( domWidth < inWidth ? domWidth / inWidth : 1 );
};

BC_VerticalArchTransformation.prototype = {

  apply( point ) {

    const inMX = this._inMX;
    const DX = ( ( point.x - inMX ) * this._scale ) ;
    const U  = DX / this._domWidth ;
    const L  = U * this._xCircleArcLen ;
    const W  = L / this._xAxis ;

    const phi = this._arcMiddle + ( this._upper ? - W : + W ) ;

    const DY = this._yAxis * Math.sin(phi) ;

    // This scales agains the Y=0 axis. The process also transforms the
    // anchor so positioning is respected even if the text baseline is not
    // at zero
    var posY = point.y;
    if( this._isometric ) {
      posY *= this._scale;
    }

    return new Point( inMX + DX, posY + DY );
  }
};


const VerticalArchTransformation = function( inLX, inHX, ellWidth, ellHeight, arcStartDeg, arcEndDeg, isometric ) {

  this._isometric = isometric === undefined ? false : isometric;

  // g.assert( inLX < inHX ) ;

  // http://www.mathopenref.com/coordparamellipse.html

  // g.assert( inLX < inHX ) ;

  this._inMX = ( inLX + inHX ) / 2 ;

  const arcStart = deg2rad(arcStartDeg),
        arcEnd   = deg2rad(arcEndDeg);

  // g.assert(arcStart >= 0 && arcStart <= 2 * Math.PI ) ;
  // g.assert(arcEnd   >= 0 && arcEnd   <= 2 * Math.PI ) ;

  // g.assert( ( arcStart <= Math.PI && arcStart <= Math.PI ) ||
  //            ( arcStart >  Math.PI && arcStart >  Math.PI ) ) ;

  this._upper = arcStart > arcEnd ;

  // g.assert( upper ? arcStart > arcEnd : arcStart < arcEnd ) ;

  const xAxis = this._xAxis = ellWidth  / 2 ;
                this._yAxis = ellHeight / 2 ;

  const domLX = xAxis * Math.cos(arcStart) ;
  const domHX = xAxis * Math.cos(arcEnd) ;

  // g.assert(domLX < domHX ) ;

  const domWidth = this._domWidth = domHX - domLX ;
  this._domMiddle = (domHX + domLX) / 2;

  const inWidth = inHX - inLX ;

  this._scale = ( domWidth < inWidth ? domWidth / inWidth : 1 );
};

VerticalArchTransformation.prototype = {

  apply( point ) {
    const inMX = this._inMX;
    const dX = ( ( point.x - inMX ) * this._scale ) ;
    const u  = ( dX - this._domMiddle ) / this._xAxis ;

    let w = 1 - u*u;
    if( w < 0 ) {
      w = 0;
    }
    const dY = this._yAxis * Math.sqrt( w ) *
             ( this._upper ? 1 : -1 );

    // This scales agains the Y=0 axis. The process also transforms the
    // anchor so positioning is respected even if the text baseline is not
    // at zero
    var posY = point.y;
    if( this._isometric ) {
      posY *= this._scale;
    }

    return new Point( inMX + dX, posY + dY );
  }
};


const VerticalArch = Process.VerticalArch = Process.extend({ typeName: 'VerticalArchProcess',
  type: 'VerticalArch',
  Properties: {
    height   : { type: 'magnitude' , def: new Magnitude(0,'mm')   , onChange: invalidateOwner, dropDef:true },
    width    : { type: 'magnitude' , def: new Magnitude(0,'mm')   , onChange: invalidateOwner, dropDef:true },
    from     : { type: 'angle'     , def: 170                     , onChange: invalidateOwner, dropDef:true },
    to       : { type: 'angle'     , def: 10                      , onChange: invalidateOwner, dropDef:true },
    mode     : { type: 'string'    , def: 'Legacy'                , onChange: invalidateOwner, dropDef:true },  // Or Ellipse
    isometric: { type: 'boolean'   , def: false                   , onChange: invalidateOwner, dropDef:true }
  },

  isInverted() {
    return this._from < this._to || this._width === 0 || this._height === 0 ;
  },

  canProcess( /*figure*/) {
    return Config.localVerticalArch ;
  },

  createTransform( bounds ) {
    const Transform = this._mode === 'Legacy' ? BC_VerticalArchTransformation : VerticalArchTransformation;
    return new Transform( bounds.lx, bounds.hx,
                          this._width, this._height,
                          this._from, this._to,
                          this._isometric );
  },

  process( c ) {

    const figures = c.figures;

    const shapes = toGShapesAndRasterBounds(figures);

    const bounds = computeVectorBounds(shapes);

    const outFigures = [];

    const arch = this.createTransform(bounds);

    forEach( shapes, source => {
      // CDL doesn't expose highResolution and keepLines for this process
      // We will port that if needed later
      const target = convertSegmentsToBeziers(source);
      if( target ) {

        modifyCurves( target, c => {
          c.transform(arch);
        });

        outFigures.push(target);
      }
    });

    // Transform char basic points
    forEach( c.textLayouts, layout => {
      layout.transformBasicPoints(arch);
    });

    const anchor = arch.apply( c.anchor );

    setRenderFrame( outFigures, Frame.fromBounds( computeVectorBounds(outFigures) ) );

    c.figures = outFigures;
    c.anchor = anchor;
  },
  maxHeightForBounds( bounds,textHeight) {
    return _VerticalArch_maxHeightForBounds(bounds,textHeight,this._to);
  }
});
Xml.addFromBuild(VerticalArch);


// areaHeight = ( 1-sin(to) ) * height + 2 * textHeight
// areaWidth  = cos(to) * width

const _VerticalArch_maxHeightForBounds = VerticalArch.maxHeightForBounds = function(bounds,textHeight,to) {
  return ( 2 * ( bounds.height - textHeight ) ) / ( 1 - Math.abs(Math.sin(deg2rad(to))) );
};

const _VerticalArch_maxWidthForBounds = VerticalArch.maxWidthForBounds = function(bounds,textHeight,to) {
  return bounds.width / Math.abs(Math.cos(deg2rad(to)));
};

VerticalArch.createForBounds = function(bounds,textHeight,to) {
  if( to === undefined ) {
    to = 10;
  }
  return new VerticalArch({ width : _VerticalArch_maxWidthForBounds(bounds,textHeight,to),
                            height: _VerticalArch_maxHeightForBounds(bounds,textHeight,to),
                            to: to, from: 180-to });
};

// Perspective
//===============================================================================================

const Perspective = Process.Perspective = Process.extend({ typeName: 'PerspectiveProcess',
  type: 'Perspective',
  Properties: {
    hPoint : { type: 'Point' , def: null, onChange: invalidateOwner, dropDef:true },
    vPoint : { type: 'Point' , def: null, onChange: invalidateOwner, dropDef:true }
  }
});
Xml.addFromBuild(Perspective);


// FitEnvelope
//===============================================================================================

function computeOutScale( x1,y1, x2,y2, transform, original ) {
  return Point.substract( transform.apply( new Point(x1,y1) ) ,
                          transform.apply( new Point(x2,y2) ) ).norm() / original;
}

function constructMixedFreeformCoonsPatch( envelopeCurves ) {
  const d = new MixedFreeformPolyCurve( envelopeCurves.d );
  const u = new MixedFreeformPolyCurve( envelopeCurves.u );
  const l = new MixedFreeformPolyCurve( envelopeCurves.l );
  const r = new MixedFreeformPolyCurve( envelopeCurves.r );
  return new CoonsPatch(d,u,l,r);
}

function constructFreeformCoonsPatch( envelopeCurves ) {
  const d = new FreeformPolyCurve( envelopeCurves.d );
  const u = new FreeformPolyCurve( envelopeCurves.u );
  const l = new FreeformPolyCurve( envelopeCurves.l );
  const r = new FreeformPolyCurve( envelopeCurves.r );
  return new CoonsPatch(d,u,l,r);
}


function toVectorOfPoints( curve ) {
  return curve.polygonize(0.1,2);
}

function toUniformWalkPolylineCurve( curve ) {
  const convertedPoints = toVectorOfPoints(curve) ;
  return new WalkPolylineCurve(convertedPoints,'Uniform');
}

function constructUniformCoonsPatch( envelopeCurves ) {
  const d = toUniformWalkPolylineCurve( envelopeCurves.d );
  const u = toUniformWalkPolylineCurve( envelopeCurves.u );
  const l = toUniformWalkPolylineCurve( envelopeCurves.l );
  const r = toUniformWalkPolylineCurve( envelopeCurves.r );
  return new CoonsPatch(d,u,l,r);
}

function toLinearWalkPolylineCurve( curve, dir ) {
  const convertedPoints = toVectorOfPoints(curve) ;
  return new WalkPolylineCurve(convertedPoints,'Linear',dir);
}

function constructBoxedCoonsPatch( envelopeCurves ) {
  const horizontalDir = new Point(1.0,0.0);
  const verticalDir   = new Point(0.0,1.0);
  const d = toLinearWalkPolylineCurve( envelopeCurves.d, horizontalDir );
  const u = toLinearWalkPolylineCurve( envelopeCurves.u, horizontalDir );
  const l = toLinearWalkPolylineCurve( envelopeCurves.l, verticalDir   );
  const r = toLinearWalkPolylineCurve( envelopeCurves.r, verticalDir   );
  return new CoonsPatch(d,u,l,r);
}


const MeshWrap = function(coonsPatch,bounds) {
  this._bounds = bounds;
  this._coonsPatch = coonsPatch;
};
MeshWrap.prototype = {
  apply( point ) {
    const b = this._bounds;

    let tx = ( point.x - b.lx ) / b.width,
        ty = ( point.y - b.ly ) / b.height;

    if( tx < 0 ) { tx = 0; } else if( tx > 1 ) { tx = 1; }
    if( ty < 0 ) { ty = 0; } else if( ty > 1 ) { ty = 1; }

    return this._coonsPatch.eval(tx,ty);
  }
};


// From ScisoftEngine/MeshWrap/coons_patch_impl.hpp
// From ScisoftEngine/MeshWrap/envelope.hpp
// Envelope and CoonsPatch are merged compared to the engine

var CoonsPatch = function(d,u,l,r) {
  this.d = d;
  this.u = u;
  this.l = l;
  this.r = r;
  this.P00 = d.first();
  this.P01 = d.last();
  this.P10 = u.first();
  this.P11 = u.last();
};
CoonsPatch.prototype = {

  eval( u, v ) {
    const mu = 1 - u ,
        mv = 1 - v ;

    const du = this.d.eval(u);
    const uu = this.u.eval(u);
    const lv = this.l.eval(v);
    const rv = this.r.eval(v);

    //   (1-v)*CurveD(u) + v*CurveU(u)    ---> col
    // + (1-u)*CurveL(v) + u*CurveR(v)    ---> row
    // - | (1-u) u | * | mP00 mP01 | * | (1-v) v |
    //                 | mP10 mP11 |

    const col =  du.sm( mv ).add( uu.sm( v ) ) ;
    const row =  lv.sm( mu ).add( rv.sm( u ) ) ;

    const q = ( Point.sm( this.P00, mu ).add( Point.sm( this.P01, u ) ) ).sm( mv )
      .add( ( Point.sm( this.P10, mu ).add( Point.sm( this.P11, u ) ) ).sm( v  ) );

    const p = Point.add(col,row).substract(q);

    return p;
  }
};


// From ScisoftEngine/MeshWrap/cubic_bezier_polycurve.hpp

const PieceData = function(piece,localLen, totalLen) {
  this.piece    = piece;
  this.localLen = localLen;
  this.totalLen = totalLen;
};

// Pieces are maintained in a tree-like structure for fast matching of parameter values->pieces.

const MixedFreeformPolyCurve = function(curve) {
  const pieces = this._pieces = curve.pieces;

  const piecesData = [];

  let totalLen = 0;
  for(let k = 0, kEnd = pieces.length; k < kEnd; ++k) {
    const piece = pieces[k];
    const localLen = piece.arcLength();
    totalLen += localLen;
    piecesData.push( new PieceData(piece,localLen,totalLen) ) ;
  }

  this._arcLen = totalLen;
  this._piecesData = piecesData;
};

MixedFreeformPolyCurve.prototype = {
  eval( t ) {
    const l = t * this._arcLen;
    /* Piece_Vector::const_iterator u = std::upper_bound(mPieces.begin()
                                                        ,mPieces.end()
                                                        ,Piece::CreateWithTotalLen(lL)
                                                        ,Piece::ComesAfter()
                                                        ); */
    const piecesData = this._piecesData;
    const n = sortedIndexBy( piecesData, { totalLen: l }, pd => pd.totalLen );
    const pieceData = piecesData[n];

    if( pieceData ) {
      const localLen = pieceData.totalLen - l;
      const localT   = 1 - ( localLen / pieceData.localLen );
      return pieceData.piece.eval(localT);
    }
    else {
      return last(this._pieces).eval(1);
    }
  },

  first() {
    return this._pieces[0].P0;
  },
  last() {
    return last(this._pieces).last();
  }
};

const FreeformPolyCurve = function(curve) {
  this._pieces = curve.pieces;
};
FreeformPolyCurve.prototype = {
  eval( t ) {
    const pieces = this._pieces, piecesN = pieces.length;
    const l = t * piecesN;
    const pieceIndex = Math.floor( l );
    if( pieceIndex < piecesN ) {
      const localT = l - pieceIndex;
      return pieces[pieceIndex].eval(localT);
    }
    else {
      return last(pieces).eval(1);
    }
  },
  first() {
    return this._pieces[0].P0;
  },
  last() {
    return last(this._pieces).last();
  }
};


// From ScisoftEngine/MeshWrap/walk_polyline_curve.hpp

// A quick eval polyline bezier curve with uniform walk over the path.
var WalkPolylineCurve = function( curve, type, dir ) {
  this._curve = curve;
  switch(type) {
    case 'Uniform':
      this.distance = this._uniformDistance;
      break;
    case 'Linear':
      this._dir = dir.normalized();
      this.distance = this._linearDistance;
      break;
  }
  this._preprocess();
};
WalkPolylineCurve.prototype = {

// Diferent distances strategies

// type 'uniform'
  _uniformDistance: Point.distance,

// type 'linear'
  _linearDistance( pa, pb ) {
    return Math.abs( Point.dotProduct( pb.minus(pa), this._dir ));
  },

  first() {
    return this._curve[0];
  },
  last() {
    return this._curve[this._curve.length-1];
  },

  get bounds() {
    return this._curve.bounds;
  },

  eval( t ) { // 0 <= t <= 1
    const curve          = this._curve,
          curveSize      = curve.length,
          partialLength  = this._partialLength,
          segmentsLength = this._segmentsLength;

    const lt = t * this._curveLength;

    const i = sortedIndex(partialLength,lt);

    if( i >= curveSize ) {
      return curve[curveSize-1].clone();
    }
    else if( i <= 0 ) {
      return curve[0].clone();
    }
    else {
      const segLength = segmentsLength[i-1];
      const u = ( lt - partialLength[i-1] ) / segLength;
      return Point.sm( curve[i-1], 1-u ).add( Point.sm( curve[i], u ) );
    }
  },

  curveLength() {
    return this._curveLength;
  },

  _preprocess() {
    const curve = this._curve;
    const curveSize = curve.length;

    let curveLength = 0;
    const segmentsLength = [];
    const partialLength  = [];

    let p = curve[0];

    for(let k = 1; k < curveSize; ++k ) {
      const q = curve[k];
      const dis = this.distance(q,p);
      segmentsLength[k-1] = dis;
      partialLength [k-1] = curveLength;
      curveLength += dis;
      p = q;
    }
    segmentsLength[curveSize-1] = 0;
    partialLength [curveSize-1] = curveLength;

    this._curveLength    = curveLength;
    this._segmentsLength = segmentsLength;
    this._partialLength  = partialLength;
  }
};

function computeMinMaxCurve2CurveDistance(a,b) {
  const a_arcLen = a.arcLength();
  const b_arcLen = b.arcLength();

  const cSamples = 10;

  const da = a_arcLen / cSamples;
  const db = b_arcLen / cSamples;

  const cursorA = new CurveArcLenCursor(a);
  const cursorB = new CurveArcLenCursor(b);

  let min2 = Number.MAX_VALUE,
      max2 = Number.MIN_VALUE;

  for( let i = 0 ; i < cSamples ; ++ i ) {
    const PA = cursorA.pos().point,
          PB = cursorB.pos().point;

    const d2 = Point.distance2(PA,PB);
    if( d2 < min2 ) {
      min2 = d2;
    }
    if( d2 > max2 ) {
      max2 = d2;
    }

    cursorA.move(da);
    cursorB.move(db);
  }

  return { min: Math.sqrt(min2), max: Math.sqrt(max2) };
}

let MeshWrapTransformation = function( envelopeCurves, bounds, mode ) {
  this._envelopeCurves = envelopeCurves;
  this._bounds         = bounds;
  this._mode           = mode;
  this._meshWrap       = this.createMeshWrap();
};

MeshWrapTransformation.prototype = {

  apply( point ) {
    const mw = this._meshWrap;
    if( mw ) {
      return mw.apply(point);
    }
    else {
      return point;
    }
  },
  createCoonsPatch() {
    const envelopeCurves = this._envelopeCurves;
    switch( this._mode ) {
      case 'Uniform' : return constructUniformCoonsPatch(envelopeCurves);
      case 'Boxed'   : return constructBoxedCoonsPatch(envelopeCurves);
      case 'Freeform': return constructFreeformCoonsPatch(envelopeCurves);
      default:
      case 'Mixed'   : return constructMixedFreeformCoonsPatch(envelopeCurves);
    }
  },
  createMeshWrap() {
    const coonsPatch = this.createCoonsPatch();
    return new MeshWrap(coonsPatch,this._bounds);
  }
};


function setRenderFrame( figures, frame ) {
  for(let k = 0, kEnd = figures.length; k < kEnd; ++k ) {
    const figure = figures[k];
    const { polyregions } = figure;
    for(let p = 0, pEnd = polyregions.length; p < pEnd; ++p ) {
      polyregions[p]._renderFrame = frame.clone();
    }
  }
}

function computeEnvelopeSize(envelopeCurves) {
  return {
    width : computeMinMaxCurve2CurveDistance(envelopeCurves.l, envelopeCurves.r) ,
    height: computeMinMaxCurve2CurveDistance(envelopeCurves.d, envelopeCurves.u)
  };
}

function toGShapesAndRasterBounds( figures ) {
  const rFigures = [];
  for(let i = 0, iEnd = figures.length; i < iEnd; i++ ) {
    const figure = figures[i];
    rFigures.push( figure.toGShape ? figure.toGShape() : figure.clone() );
  }
  return rFigures; // [TODO] Raster .toGShape()
}

function computeVectorBounds( figures ) {
  const bb = new BoundsBuilder();
  for(let k = 0, kEnd = figures.length; k < kEnd; ++k ) {
    bb.update( figures[k].vectorBounds() );
  }
  return bb.bounds;
}

function computeBounds( figures ) {
  const bb = new BoundsBuilder();
  for(let k = 0, kEnd = figures.length; k < kEnd; ++k ) {
    bb.update( figures[k].bounds );
  }
  return bb.bounds;
}

const FitEnvelope = Process.FitEnvelope = Process.extend({ typeName: 'FitEnvelopeProcess',

  type: 'FitEnvelope',

  Properties: {
    mode           : { type: 'string'  , def: 'Mixed'     , dropDef: true, onChange: invalidateOwner },
    vPos           : { type: 'string'  , def: 'Stretched' , onChange: invalidateOwner },
    hPos           : { type: 'string'  , def: 'Stretched' , onChange: invalidateOwner },
    highResolution : { type: 'boolean' , def: false , onChange: invalidateOwner },
    keepLines      : { type: 'boolean' , def: false , onChange: invalidateOwner },
    addBearingX    : { type: 'boolean' , def: false , dropDef: true, onChange: invalidateOwner },
    stableBaseline : { type: 'boolean' , def: false , dropDef: true, onChange: invalidateOwner }
  },

  init( c ) {
    this.callParent(c);
    this._envelope = (c&&c.envelope) || null;
  },

  isEqual( other ) {
    return this.propertiesAreEqual(other) &&
           this._envelope.isEqual(other._envelope);
  },

  envelope( v ) {
    return v===undefined? this._envelope :(this._envelope=v,this.invalidate(),this);
  },

  setupXml( node ) {
    Xml.setupProperties(node,this);
    this._envelope.buildXml(node);
  },

  equalizeFor$( figure ) {
    return figure.preVectorBounds$() .then( uBounds => {
    // Equalize the envelope to fit the current bounds of the figure
    return this._envelope.setSize({height:uBounds.height});
    });
  },

  clone() {
    const c = new FitEnvelope(this);
    c.envelope( this._envelope.clone() );
    return c;
  },

  canProcess( figure ) {
    return Config.localFitEnvelope &&
           this._highResolution === false &&
           ( ! ( ( this._addBearingX || this._stableBaseline ) && figure.isText && figure._textParts.length > 0 ) );
  },

  process( c, forcedAnchor, useEnvelopeFrame = true ) {
    const { figures } = c;

    const shape = toSingleGShape(figures);

    let bounds = shape.vectorBounds();

    const envelope = this._envelope;

    const envelopeFrame = Frame.fromBounds( envelope.vectorBounds() );

    const envelopeCurves = {
      d: envelope.d(),
      r: envelope.r(),
      u: envelope.u().reversed(),
      l: envelope.l().reversed()
    };
    const envelopeSize = computeEnvelopeSize(envelopeCurves);

    const stableBaseline = this._stableBaseline, addBearingX = this._addBearingX;
    if( stableBaseline || addBearingX ) {
      const layout = figures[0];
      if( layout instanceof TextLayout ) {
        const textLine = head(layout.textLines());
        if( stableBaseline ) {
          const baselineY = textLine._baseline.P0.x;
          const ascent = layout.ascent();
          const descent = layout.descent();
          bounds = new Bounds( bounds.lx, Math.min(bounds.ly,baselineY-descent) ,
                               bounds.hx, Math.max(bounds.hy,baselineY+ascent ) );
        }
        else if( addBearingX ) {
          const bearingLX = textLine.bearingLX();
          if( bearingLX > 0 ) {
            bounds = bounds.clone();
            bounds.lx -= bearingLX;
          }
        }
      }
    }

    let scale = 1;

    const originalBounds = bounds.clone();

    const hPos = this._hPos, vPos = this._vPos;
    const hPosStretched = ( hPos === 'Stretched' ),
          vPosStretched = ( vPos === 'Stretched' ),
          hPosExpanded  = ( hPos === 'Expanded'  ),
          vPosExpanded  = ( vPos === 'Expanded'  );

    if( hPosStretched && ! vPosStretched ) {
      scale = envelopeSize.width.max / bounds.width;
      bounds.scalingTransform(Matrix.scale(scale));
    }

    if( vPosStretched && ! hPosStretched ) {
      scale = envelopeSize.height.max / bounds.height;
      bounds.scalingTransform(Matrix.scale(scale));
    }

    bounds = bounds.clone();
    const w = bounds.width;
    const h = bounds.height;

    const minWidth  = envelopeSize.width.min;
    const minHeight = envelopeSize.height.min;

    if( w < minWidth && ( ! hPosStretched && ! hPosExpanded ) ) {
      const gapH = minWidth - w;

      let gapL = 0, gapR = 0;
      switch( hPos ) {
        case 'Right'  : gapL = gapH ; gapR = 0 ; break ;
        case 'Center' : gapL = gapR = gapH / 2 ; break ;
        case 'Left'   : gapL = 0 ; gapR = gapH ; break ;
      }
      bounds.lx -= gapL;
      bounds.hx += gapR;
    }

    if( h < minHeight && ( ! vPosStretched && ! vPosExpanded ) ) {
      const gapV = minHeight - h;

      let gapB = 0, gapT = 0;
      switch( vPos ) {
        case 'Top'    : gapB = gapV ; gapT = 0 ; break ;
        case 'Center' : gapB = gapT = gapV / 2 ; break ;
        case 'Bottom' : gapB = 0 ; gapT = gapV ; break ;
      }
      bounds.ly -= gapB;
      bounds.hy += gapT;
    }

    const outFigures = [];

    const wrap = new MeshWrapTransformation(envelopeCurves,bounds,this._mode);

    const target = preprocessInput(shape,this._highResolution,this._keepLines); // [TODO], used for highResolution: this._polyParams) ;
    if( target ) {

      if( scale !== 1 ) {
        modifyCurves( target, c => {
          c.transform(Matrix.scale(scale));
        });
      }

      modifyCurves( target, c => {
        c.transform(wrap);
      });

      outFigures.push(target);
    }

    // Transform char basic points
    forEach( c.textLayouts, layout => {
      if( scale !== 1 ) {
        layout.transformBasicPoints(Matrix.scale(scale));
      }
      layout.transformBasicPoints(wrap);
    });

    // We respect the alignment decided in the envelope options
    let anchor = forcedAnchor || envelopeFrame.center;

    // Approximate deformation
    //   o --------- o --------- o
    //   |                       |
    //   o --------- o --------- o
    //
    const originalWidth  = originalBounds.width,
          originalHeight = originalBounds.height;

    // bounds is already a scaled version of originalBounds
    const lx = bounds.lx, mx = bounds.mx, hx = bounds.hx,
          ly = bounds.ly, my = bounds.my, hy = bounds.hy;

    var outScaleX = Math.min(
      computeOutScale( lx,ly, hx,ly, wrap, originalWidth ),
      computeOutScale( lx,my, hx,my, wrap, originalWidth ),
      computeOutScale( lx,hy, hx,hy, wrap, originalWidth )
    );

    var outScaleY = Math.min(
      computeOutScale( lx,ly, lx,hy, wrap, originalHeight ),
      computeOutScale( mx,ly, mx,hy, wrap, originalHeight ),
      computeOutScale( hx,ly, hx,hy, wrap, originalHeight )
    );

    c.preScale = new Point( c.preScale.x * outScaleX, c.preScale.y * outScaleY );

    var renderFrame = ( useEnvelopeFrame || ! target ) ? envelopeFrame : Frame.fromBounds(target.vectorBounds());

    setRenderFrame( outFigures, renderFrame );

    // [TODO] Expanded options needs to be checked... There is a new option added to the process
    //        called FitEnvelopeToInput. It seems they are very similar.
    if( hPosExpanded || vPosExpanded ) {
      const hScale = hPosExpanded ? bounds.width  / envelopeFrame.width  : 1;
      const vScale = vPosExpanded ? bounds.height / envelopeFrame.height : 1;
      const scaling = Matrix.scale(hScale,vScale);
      forEach( outFigures, figure => {
        modifyCurves( figure, c => {
          c.transform(scaling);
        });
      });
      anchor = scaling.apply(anchor);
    }


    /* [TODO]
    if( Config.debugEnvelope ) {
      GRegion lEnvRegion = new GRegion(lEnv.CurveD.Copy());
      lEnvRegion.AddHole(lEnv.CurveR.Copy());
      lEnvRegion.AddHole(lEnv.CurveU.Copy());
      lEnvRegion.AddHole(lEnv.CurveL.Copy());
      GMultiRegion lEnvMR = new GMultiRegion(lEnvRegion);
      lEnvMR.FillMode = GFillMode.Alternate ;
      lEnvMR.AssignStyle(new GColorPen(GColor.Black),null);
      ShapeFigure lEnvFig = new ShapeFigure( new GShape(lEnvMR) ) ;
      aOutput.Add(lEnvFig);
    }
    */

    c.figures = outFigures;
    c.anchor = anchor;
  }
});

FitEnvelope.parseXml = function( node ) {
  const p = Xml.parseProperties(node,FitEnvelope);
  p.envelope = Envelope.fromXml(node);
  if( p.vPos === 'Middle' ){
    p.vPos = 'Center';
  }
  return new FitEnvelope(p);
};

Xml.addFromBuild(FitEnvelope);

FitEnvelope.createFromFigure = function( figure ) {
  return new FitEnvelope({ vPos: 'Stretched', hPos: 'Stretched',
                           envelope: Envelope.createFromFigure(figure) });
};



// SmartEnvelope
//=============================================================================

// [TODO] convert to js naming conventions

var CreateItalicEnvelope = function( aBBox, aHeight, aExtraAscentHeight, aExtraDescentHeight ) {
  // We need to divide the calculation so the envelope width for the reference size characters
  // remains equal to the width of the bbox
  var lFactor = Math.tan( deg2rad(71) );

  var lHeightOffsetX = aHeight / lFactor;
  var lExtraAscentHeightOffsetX = aExtraAscentHeight / lFactor;
  var lExtraDescentHeightOffsetX = aExtraDescentHeight / lFactor;

  var lLXLY = aBBox.lx_ly .plus( new Point( - lExtraDescentHeightOffsetX, 0 ) );
  var lHXLY = aBBox.hx_ly .plus( new Point( - lHeightOffsetX - lExtraDescentHeightOffsetX, 0 ) );
  var lHXHY = aBBox.hx_hy .plus( new Point( lExtraAscentHeightOffsetX, 0 ) );
  var lLXHY = aBBox.lx_hy .plus( new Point( lExtraAscentHeightOffsetX + lHeightOffsetX , 0 ) );

  var lPiecesD = CreateStraightBeziers( lLXLY, lHXLY );
  var lPiecesR = CreateStraightBeziers( lHXLY, lHXHY );
  var lPiecesU = CreateStraightBeziers( lHXHY, lLXHY );
  var lPiecesL = CreateStraightBeziers( lLXHY, lLXLY );

  var lCurveD = new Curve(lPiecesD,false);
  var lCurveR = new Curve(lPiecesR,false);
  var lCurveU = new Curve(lPiecesU,false);
  var lCurveL = new Curve(lPiecesL,false);

  return new Envelope([ lCurveD, lCurveR, lCurveU, lCurveL ]);
};

var CreateStraightBeziers = function( aFrom, aTo, distanceY = 0 ) {
  // lMiddle was computed in C# as
  // GEO.CreateMidPoint(aFrom,aTo) + new GVector(0,distanceY);
  var lMiddle = new Point((aFrom.x+aTo.x)/2,(aFrom.y+aTo.y)/2+distanceY);
  return [
    Cubic.fromSegment(aFrom,lMiddle),
    Cubic.fromSegment(lMiddle,aTo)
  ];
};

var CreateArchBeziers = function( aFrom, aTo, distanceY ) {
  // Is Gpoint in C# the same as Point in JS?
  var lMiddle = new Point((aFrom.x+aTo.x)/2,aFrom.y+distanceY);
  var lControlX = ( lMiddle.x-aFrom.x ) / 3; // It will be negative if aFrom.x > aTo.x
  // Is GCubicBezierPiece in C# the same as Cubic in JS?
  return [
    new Cubic(
      aFrom,
      new Point( aFrom.x +   lControlX, aFrom.y+0.75*distanceY ),
      new Point( aFrom.x + 2*lControlX, lMiddle.y ),
      lMiddle
    ),
    new Cubic(
      lMiddle,
      new Point( lMiddle.x + lControlX, lMiddle.y ),
      new Point( lMiddle.x + 2*lControlX, aFrom.y+0.75*distanceY ),
      aTo
    )
  ];
};

var CreateSlantBeziers = function( aFrom, aTo, distanceY ) {
  var lNewFrom, lNewTo;
  if (aFrom.x < aTo.x) {
    lNewFrom = aFrom;
    lNewTo   = new Point(aTo.x,aTo.y+distanceY);
  } else {
    lNewFrom = new Point(aFrom.x,aFrom.y+distanceY);
    lNewTo   = aTo;
  }
  var lMiddle = new Point((lNewFrom.x+lNewTo.x)/2,(lNewFrom.y+lNewTo.y)/2);
  return [
    Cubic.fromSegment(lNewFrom,lMiddle),
    Cubic.fromSegment(lMiddle,lNewTo)
  ];
};

const SmartEnvelope = Process.SmartEnvelope = Process.extend({
  typeName: 'SmartEnvelopeProcess',
  type: 'SmartEnvelope',
  Properties: {

    kind           : { type: 'string'  , tag: 'Type', def: 'Straight' , onChange: invalidateOwner, dropDef:true },
    width          : { type: 'magnitude'  , def:new Magnitude(0,'mm') , onChange: invalidateOwner, dropDef:true },
    height         : { type: 'magnitude'  , def:new Magnitude(0,'mm') , onChange: invalidateOwner, dropDef:true },

    // We only exposed Type and Width for the moment in CDL, this needs to be added to CxEngine
    hPos           : { type: 'string'  , def: 'Center'   , onChange: invalidateOwner, dropDef:true },
    vPos           : { type: 'string'  , def: 'Center'   , onChange: invalidateOwner, dropDef:true },
    mode           : { type: 'string'  , def: 'FreeForm' , onChange: invalidateOwner, dropDef:true },
    highResolution : { type: 'boolean' , def: false      , onChange: invalidateOwner, dropDef:true },
    keepLines      : { type: 'boolean' , def: false      , onChange: invalidateOwner, dropDef:true },
    addBearingX    : { type: 'boolean' , def: false      , onChange: invalidateOwner, dropDef:true },
  },

  canProcess() {
    return Config.localSmartEnvelope;
  },

  // forcedAnchor is an out parameter
  createEnvelope( figures, inputAnchor ) {

    var forcedAnchor = inputAnchor.clone();

    var lBBox = computeVectorBounds( toGShapesAndRasterBounds(figures) );

    // Forced Width for the envelope
    if( this.width > 0 )
    {
      // Correct anchor so it is relative to the envelope instead of the input figure
      forcedAnchor.x = lBBox.mx + ( forcedAnchor.x - lBBox.mx ) * this.width / lBBox.width;

      lBBox = new Bounds( lBBox.mx - this.width/2, lBBox.ly ,
                          lBBox.mx + this.width/2, lBBox.hy );
    }

    // [TODO] What happens when there are multiple figures?

    var lBaselineY = 0;
    var lDescentExtraHeight = 0;
    var lAscentExtraHeight = 0;
    var lHeight = lBBox.height;

    var layout = figures[0];
    if( layout instanceof TextLayout ) {

      var lFirstLine = layout.textLines()[0];
      // The baseline is a segment, we need the y-coordinate of its origin.
      lBaselineY = lFirstLine._baseline.P0.y;

      var lAscent = layout.ascent();
      var lDescent = layout.descent();

      // The envelopes are defined against the size of the uppercase M for AutoSize: true

      // [TODO] Take into account PreTransform?

      lDescentExtraHeight = lDescent;

      lHeight = layout.sizeOfReferenceChar(); // Size of M, this should also work for AutoSize: false
      lAscentExtraHeight = lAscent - lHeight;

      lBBox = new Bounds( lBBox.lx, Math.min(lBBox.ly,lBaselineY-lDescent),
                          lBBox.hx, Math.max(lBBox.hy,lBaselineY+lAscent));
    }

    if( this.kind === 'Italic' )
    {
      // Italic doesn't work like the rest of the envelopes
      const envelope = CreateItalicEnvelope(lBBox,lHeight,lAscentExtraHeight,lDescentExtraHeight);
      return { envelope, forcedAnchor };
    }

    //     _________________________________
    //    |                                 |  lAscentExtraHeight
    //     ---------------------------------
    //    |                                 |
    //    |                                 |  lHeight
    //    |                                 |
    //     ---------------------------------
    //    |_________________________________|  lDescentExtraHeight
    //

    var lCreateCurves;
    var lUpDeformation = 0;
    var lDownDeformation = 0;

    switch( this.kind ) {

      case 'Bowtie':
        lUpDeformation   = - 0.25;
        lDownDeformation =   0.25;
        lCreateCurves = CreateStraightBeziers;
        break;

      case 'Bridge':
        lUpDeformation   = 0;
        lDownDeformation = 0.425;
        lCreateCurves = CreateArchBeziers;
        break;

      case 'DoubleBridge':
        lUpDeformation   = - 0.25;
        lDownDeformation =   0.25;
        lCreateCurves = CreateArchBeziers;
        break;

      case 'NewSlant':
        // Constant angle for all width/height
        // angle = atan( 4 / 15 ), make this a parameter?
        var lSlantDeformation = ( lBBox.width * 4 / 15 ) / lHeight;
        lUpDeformation   =  lSlantDeformation;
        lDownDeformation =  lSlantDeformation;
        lCreateCurves = CreateSlantBeziers;
        break;

      case 'Pennant':
        lUpDeformation   = -0.25;
        lDownDeformation =  0.25;
        lCreateCurves = CreateSlantBeziers;
        break;

      case 'ReverseArch':
        lUpDeformation   = -0.5;
        lDownDeformation = -0.5;
        lCreateCurves = CreateArchBeziers;
        break;

      case 'ReverseBridge':
        lUpDeformation   = -0.425;
        lDownDeformation = 0;
        lCreateCurves = CreateArchBeziers;
        break;

      case 'VerticalArch':
        lUpDeformation   = 0.5;
        lDownDeformation = 0.5;
        lCreateCurves = CreateArchBeziers;
        break;

      case 'Straight':
      default:
        lUpDeformation   = 0;
        lDownDeformation = 0;
        lCreateCurves = CreateStraightBeziers;
        break;
    }

    // Assumming baseline at 0 (it will be added later), values for the envelope
    // as defined for uppercase letters
    var lDown =           lDownDeformation * lHeight;
    var lUp   = lHeight +   lUpDeformation * lHeight;
    var lDeformationHeight = lUp - lDown;

    // Values for the real envelope so it produces the same effect
    var lDownY = lBaselineY + lDown - ( lDescentExtraHeight * ( lDeformationHeight/2 ) / ( lHeight/2 ) );
    var lUpY = lBaselineY   + lUp + ( lAscentExtraHeight * ( lDeformationHeight/2 ) / ( lHeight/2 ) );

    // Use here the lCreateCurves function assigned above.
    var lPiecesD = lCreateCurves(lBBox.lx_ly,lBBox.hx_ly, lDownY - lBBox.ly );
    var lPiecesU = lCreateCurves(lBBox.hx_hy,lBBox.lx_hy, lUpY   - lBBox.hy );
    var lPiecesR = CreateStraightBeziers(last(lPiecesD).last(),lPiecesU[0].P0);
    var lPiecesL = CreateStraightBeziers(last(lPiecesU).last(),lPiecesD[0].P0);

    var lCurveD = new Curve(lPiecesD,false);
    var lCurveR = new Curve(lPiecesR,false);
    var lCurveU = new Curve(lPiecesU,false);
    var lCurveL = new Curve(lPiecesL,false);

    return { envelope: new Envelope([ lCurveD,lCurveR,lCurveU,lCurveL ]), forcedAnchor };
  },

  process( c ) {

    const { envelope, forcedAnchor } = this.createEnvelope( c.figures, c.anchor );

    const stableBaseline = true;

    // Use FitEnvelopeProcess from deform.js
    var fitEnv = new Process.FitEnvelope({
      mode           : this.mode,
      vPos           : this.vPos,
      hPos           : this.hPos,
      highResolution : this.highResolution,
      keepLines      : this.keepLines,
      addBearingX    : this.addBearingX,
      stableBaseline,
      envelope
    });

    return fitEnv.process( c, forcedAnchor,
                           false ); // use figure bounds instead of envelope frame for render frame
  }
});

Xml.addFromBuild(SmartEnvelope);

//=============================================================================
// Skeleton for creating a new process
//=============================================================================

/*
// NewProcess
//=============================================================================

var processHelperFunctionsIfNeeded = function(/*shape, curves or whatever* /) {
  // ...
};

const NewProcess = Process.NewProcess = Process.extend({
  typeName: 'NewProcessProcess',
  type: 'NewProcess',
  Properties: {
    // ...
  },
  canProcess(/*figure* /){
    // can we apply this process locally?
    // we can rely on a variable in config.js
    return Config.localNewProcess;
    // or just
    return true;
  },
  // c: { figures, anchor, textLayouts }
  process( c ){
    // code for the process
  }
});

Xml.addFromBuild(NewProcess);


// If there are properties that needs to be parsed or cloned manually...

const NewProcess = Process.NewProcess = Process.extend({
  typeName: 'NewProcessProcess',
  type: 'NewProcess',
  Properties: {
    // ...
  },
  init( c ) {
    // optional
  },
  isEqual( other ) {
    // optional
  },
  setupXml( node ) {
    Xml.setupProperties(node,this);
    // code for building XML from this node
  },
  clone() {
    var c = new NewProcess(this);
    // code to clone this process
    return c;
  },
  canProcess(/*figure* /){
    // can we apply this process locally?
    // we can rely on a variable in config.js
    return Config.localNewProcess;
    // or just
    return true;
  },
  process( c ){
    // code for the process
  }
});

NewProcess.parseXml = function(node) {
  var p = // what to look for in the XML document
  return new NewProcess(p);
};
Xml.addFromBuild(NewProcess);
*/



/** WEBPACK FOOTER **
 ** ./src/cdl/process/deform.js
 **/